/* Autoplaying loops that actually autoplay.
 *
 * The markup already carries autoplay/loop/muted/playsinline, which is the
 * whole requirement on desktop. Mobile is stricter and, worse, silent about
 * it: iOS refuses playback when the element is off-screen or the tab was
 * restored, Low Power Mode refuses outright, and Chrome on Android drops the
 * attempt if the first frames have not buffered. In every case the browser
 * simply never retries, so the loop sits on its poster forever.
 *
 * So the attributes are re-asserted as properties (iOS honours the property
 * where it has already made up its mind about the attribute), playback is
 * driven from an IntersectionObserver rather than from page load, and a
 * refusal is retried once on the visitor's first interaction. Off-screen
 * loops pause, which is what the battery would ask for anyway.
 *
 * Reduced motion is respected by handing over the still outright rather than
 * by leaving a video parked on its poster, which on iOS is the play button
 * again. The section is authored so that it reads correctly as a still.
 *
 * When playback is refused for good, the video is REPLACED by its own poster
 * as an <img>. iOS paints a large play button over any inline video it has
 * not started, and that button is not styleable or removable, so a video that
 * will never play is worse than no video at all. Two things count as final:
 * a decode error (the file is not playable here, which is what an iPhone
 * does with the VP9 the transparent render needs), and a play() that is
 * still refused after the visitor has interacted with the page (Low Power
 * Mode). Everything else keeps retrying.
 */
(function () {
  'use strict';

  var vids = document.querySelectorAll('video[data-ak-loop]');
  if (!vids.length) return;

  var pending = [];
  var released = false;
  var GRACE = 1200;

  function onScreen(v) {
    var r = v.getBoundingClientRect();
    return r.bottom > 0 && r.top < (window.innerHeight || 0);
  }

  /* Swap the element for its poster. Carries the class, the box and the
     label across so nothing shifts and the description survives. */
  function swapToStill(v) {
    if (!v.parentNode || !v.poster) return;
    var img = document.createElement('img');
    img.src = v.poster;
    img.className = v.className;
    img.alt = v.getAttribute('aria-label') || '';
    if (v.width) img.width = v.width;
    if (v.height) img.height = v.height;
    img.setAttribute('decoding', 'async');
    v.parentNode.replaceChild(img, v);
  }

  /* Reduced motion is not a reason to leave a video sitting there: iOS paints
     its play button on any inline video it has not started, so "no animation"
     would read as "broken player". The still IS the intended render here, so
     hand it over immediately and do nothing else. */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    Array.prototype.forEach.call(vids, swapToStill);
    return;
  }

  function attempt(v) {
    // Re-assert as properties: an attribute set before the element was
    // upgraded is not always enough on iOS.
    v.muted = true;
    v.playsInline = true;
    v.loop = true;

    var p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {
        /* Refused. Queue it for the first real gesture, which is often all
           iOS wants. Only treat it as final once the visitor has genuinely
           interacted AND the loop is on screen and has still not moved after
           a grace period: an attempt that failed while off screen, or before
           the first frames buffered, would otherwise retire a video that was
           going to play perfectly well a moment later. */
        if (pending.indexOf(v) === -1) pending.push(v);
        if (!released || !onScreen(v)) return;
        window.setTimeout(function () {
          if (v.parentNode && v.paused && !v.currentTime) swapToStill(v);
        }, GRACE);
      });
    }
  }

  function retry() {
    var queue = pending.slice();
    pending.length = 0;
    queue.forEach(attempt);
  }

  function release() {
    released = true;
    retry();
  }

  /* Only the first three are user activation as far as a browser's autoplay
     policy is concerned. A scroll still triggers a retry, because it often
     coincides with the loop arriving on screen, but it is not evidence that
     the visitor has unblocked anything, so it must not be what condemns a
     video to the still. */
  ['pointerdown', 'touchstart', 'keydown'].forEach(function (evt) {
    window.addEventListener(evt, release, { once: true, passive: true });
  });
  window.addEventListener('scroll', function () { retry(); }, { once: true, passive: true });

  if (!('IntersectionObserver' in window)) {
    Array.prototype.forEach.call(vids, attempt);
    return;
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var v = entry.target;
      if (entry.isIntersecting) {
        attempt(v);
      } else if (!v.paused) {
        v.pause();
      }
    });
  }, { rootMargin: '200px 0px', threshold: 0.01 });

  Array.prototype.forEach.call(vids, function (v) {
    /* preload="metadata" means the first frames may not be ready when play()
       is called; canplay is the browser telling us it is. */
    v.addEventListener('canplay', function () {
      if (v.paused && v.dataset.akWant === '1') attempt(v);
    });

    /* A codec this browser will not take. Permanent, so do not wait for a
       gesture that cannot help. */
    v.addEventListener('error', function () { swapToStill(v); });

    io.observe(v);
  });

  /* A tab restored from the bfcache comes back paused with no event that the
     observer would see, so re-drive whatever is on screen. */
  window.addEventListener('pageshow', function () {
    Array.prototype.forEach.call(vids, function (v) {
      var r = v.getBoundingClientRect();
      if (r.bottom > 0 && r.top < window.innerHeight) attempt(v);
    });
  });
})();
